import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, type IntegrationLibraryEntry } from "@clevis/shared-types";
import { Button, Card, CardTitle, Select, cn } from "@clevis/shared-ui";
import { ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";

export function AdminIntegrations() {
  const [entries, setEntries] = useState<Array<IntegrationLibraryEntry & { id: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("all");
  const view = useListView(entries, {
    text: (e) => [e.name, e.slug, e.description, e.category],
    sorts: {
      order: (a, b) => a.order - b.order,
      name: (a, b) => a.name.localeCompare(b.name),
      category: (a, b) => a.category.localeCompare(b.category) || a.order - b.order,
    },
    defaultSort: "order",
    filter: (e) => category === "all" || e.category === category,
  });

  useEffect(() => {
    const q = query(collection(db, paths.integrationLibrary()), orderBy("order"));
    return onSnapshot(q, (snap) => {
      setEntries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as IntegrationLibraryEntry) })));
    });
  }, []);

  async function seed() {
    setBusy(true);
    setMessage("");
    try {
      const res = await call<{ count: number }>("seedIntegrationLibrary", {}, { mutation: false });
      setMessage(`Seeded ${res.count} integrations.`);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Integration Library</h1>
        <Button onClick={seed} busy={busy} variant="secondary">
          Seed / refresh library
        </Button>
      </div>
      {message ? <p className="text-xs text-ink">{message}</p> : null}
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search integrations…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">All categories</option>
            {[...new Set(entries.map((e) => e.category))].sort().map((c) => (
              <option key={c} value={c}>
                {c} ({entries.filter((e) => e.category === c).length})
              </option>
            ))}
          </Select>
        </div>
        <div className="hidden grid-cols-[minmax(0,1fr)_110px_110px] items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid">
          <SortHeader label="Integration" k="name" view={view} />
          <SortHeader label="Category" k="category" view={view} />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-steel">Connects by</span>
        </div>
        {view.page.map((e) => (
          <div key={e.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-steel/10 px-4 py-2 last:border-0 sm:grid-cols-[minmax(0,1fr)_110px_110px]">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-medium text-ink">
                {e.name}
                {!e.enabled ? <span className="rounded-full bg-danger/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-danger">off</span> : null}
              </div>
              <div className="truncate text-xs text-steel">{e.description}</div>
            </div>
            <span className="rounded-full bg-steel/15 px-2 py-0.5 text-center text-xs capitalize text-ink">{e.category}</span>
            <span className={cn("hidden text-xs text-steel sm:block")}>{e.authKind === "api_key" ? "API key" : e.authKind === "oauth" ? "Sign-in" : "Nothing"}</span>
          </div>
        ))}
        {view.filtered.length === 0 && entries.length > 0 ? <div className="px-4 py-8 text-center text-sm text-steel">Nothing matches.</div> : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="integrations" />
        {entries.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">
            Library is empty — press "Seed / refresh library".
          </div>
        ) : null}
      </Card>
      <Card>
        <CardTitle>How it works</CardTitle>
        <p className="text-xs leading-relaxed text-steel">
          Define an integration once here, then enable it per customer from their detail page. The customer
          only enters their credentials — encrypted server-side — and they're connected. Payroll providers,
          accounting systems and the FieldPulse migration tool all flow through this same library.
        </p>
      </Card>
    </div>
  );
}
